import { useEffect, useRef, useState } from "react";

// ============================================
// DEVELOPER TOGGLE
// Set to false to disable the single-tab guard (for development)
// Set to true to enforce single-tab usage across all browsers/devices
// ============================================
//export const ENABLE_SINGLE_TAB_GUARD = true;
export const ENABLE_SINGLE_TAB_GUARD = false;

export const BROADCAST_CHANNEL_NAME = "suprabhaatham_admin_single_tab_channel";
const STORAGE_KEY = "suprabhaatham_admin_single_tab_heartbeat";
const TAB_ID_SESSION_KEY = "suprabhaatham_admin_tab_id";
const HEARTBEAT_INTERVAL_MS = 1000;
const HEARTBEAT_STALE_MS = 3000;

type Heartbeat = {
  tabId: string;
  birthTime: number;
  timestamp: number;
};

const generateTabId = (): string => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // ignore
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const getOrCreateTabId = (): string => {
  try {
    const existing = sessionStorage.getItem(TAB_ID_SESSION_KEY);
    if (existing) return existing;
    const id = generateTabId();
    sessionStorage.setItem(TAB_ID_SESSION_KEY, id);
    return id;
  } catch {
    return generateTabId();
  }
};

const readHeartbeat = (): Heartbeat | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.tabId === "string" &&
      typeof parsed.timestamp === "number" &&
      typeof parsed.birthTime === "number"
    ) {
      return parsed as Heartbeat;
    }
    return null;
  } catch {
    return null;
  }
};

const writeHeartbeat = (hb: Heartbeat): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(hb));
  } catch {
    // ignore
  }
};

const clearHeartbeat = (ownTabId: string): void => {
  const current = readHeartbeat();
  if (current && current.tabId === ownTabId) {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }
};

const isStale = (hb: Heartbeat): boolean =>
  Date.now() - hb.timestamp > HEARTBEAT_STALE_MS;

export const useSingleTabGuard = (): { isBlocked: boolean } => {
  const [isBlocked, setIsBlocked] = useState(false);
  const tabIdRef = useRef<string>("");
  const birthTimeRef = useRef<number>(0);

  useEffect(() => {
    if (!ENABLE_SINGLE_TAB_GUARD) return;

    tabIdRef.current = getOrCreateTabId();
    // performance.now ensures monotonic but we want cross-tab comparable -> Date.now + random fraction as tiebreaker
    birthTimeRef.current = Date.now() + Math.random();
    const tabId = tabIdRef.current;
    const birthTime = birthTimeRef.current;

    let blocked = false;
    const block = () => {
      if (blocked) return;
      blocked = true;
      setIsBlocked(true);
    };

    // ---- Primary: BroadcastChannel (Chrome/Edge/Firefox, Safari 15.4+) ----
    let channel: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== "undefined") {
      try {
        channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      } catch {
        channel = null;
      }
    }

    if (channel) {
      channel.onmessage = (event) => {
        const data = event.data || {};
        if (!data || data.from === tabId) return;

        if (data.type === "PING") {
          // Respond so the newcomer knows we exist
          channel?.postMessage({
            type: "PONG",
            from: tabId,
            birthTime,
          });
          // Older tab wins: if we arrived later than them, we should yield
          if (
            typeof data.birthTime === "number" &&
            data.birthTime < birthTime
          ) {
            block();
          }
        } else if (data.type === "PONG") {
          // Someone else is alive. Older one wins.
          if (
            typeof data.birthTime === "number" &&
            data.birthTime < birthTime
          ) {
            block();
          }
        }
      };

      channel.postMessage({ type: "PING", from: tabId, birthTime });
    }

    // ---- Fallback + reinforcement: localStorage heartbeat ----
    // This also defends against older browsers and private modes where
    // BroadcastChannel may be restricted.
    let heartbeatInterval: ReturnType<typeof setInterval> | null = null;

    const existing = readHeartbeat();
    if (
      existing &&
      existing.tabId !== tabId &&
      !isStale(existing) &&
      existing.birthTime < birthTime
    ) {
      block();
    } else {
      writeHeartbeat({ tabId, birthTime, timestamp: Date.now() });
    }

    heartbeatInterval = setInterval(() => {
      const current = readHeartbeat();

      if (blocked) {
        // Auto-unblock when the winner tab closes (heartbeat gone or stale)
        const winnerGone = !current || current.tabId === tabId || isStale(current);
        if (winnerGone) {
          blocked = false;
          setIsBlocked(false);
          writeHeartbeat({ tabId, birthTime, timestamp: Date.now() });
        }
        return;
      }

      if (
        current &&
        current.tabId !== tabId &&
        !isStale(current) &&
        current.birthTime < birthTime
      ) {
        block();
        return;
      }
      writeHeartbeat({ tabId, birthTime, timestamp: Date.now() });
    }, HEARTBEAT_INTERVAL_MS);

    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      try {
        const data = JSON.parse(e.newValue) as Heartbeat;
        if (
          data &&
          data.tabId !== tabId &&
          !isStale(data) &&
          data.birthTime < birthTime
        ) {
          block();
        }
      } catch {
        // ignore
      }
    };

    const onUnload = () => {
      clearHeartbeat(tabId);
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("beforeunload", onUnload);
    window.addEventListener("pagehide", onUnload);

    return () => {
      if (heartbeatInterval) clearInterval(heartbeatInterval);
      if (channel) {
        try {
          channel.close();
        } catch {
          // ignore
        }
      }
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("beforeunload", onUnload);
      window.removeEventListener("pagehide", onUnload);
      clearHeartbeat(tabId);
    };
  }, []);

  return { isBlocked };
};

export const SingleTabBlocker = () => {
  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483647,
        background: "rgba(0, 0, 0, 0.95)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <div
        style={{
          textAlign: "center",
          color: "#fff",
          padding: 40,
          maxWidth: 520,
        }}
      >
        <div style={{ fontSize: 64, marginBottom: 20 }}>⚠️</div>
        <h2
          style={{
            fontSize: 24,
            marginBottom: 16,
            color: "#ff6b6b",
            fontWeight: 600,
          }}
        >
          Application Already Open
        </h2>
        <p
          style={{
            fontSize: 16,
            lineHeight: 1.6,
            marginBottom: 12,
            color: "#ccc",
          }}
        >
          This application is already running in another tab or window.
        </p>
        <p
          style={{
            fontSize: 14,
            lineHeight: 1.6,
            marginBottom: 24,
            color: "#ffcc00",
          }}
        >
          Only one tab is allowed at a time. Close the other tab — this tab will resume automatically.
        </p>
        <button
          onClick={handleReload}
          style={{
            background: "#0055A6",
            color: "#fff",
            border: "none",
            padding: "12px 32px",
            fontSize: 16,
            borderRadius: 4,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Reload
        </button>
      </div>
    </div>
  );
};
